import Assign from 'core-js-pure/features/object/assign';
import { KustomerCoreWindow, KustomerCore } from 'core/types';
import {
  appendElementToDocument,
  getSafeParentContext,
  setupIframeForApi,
} from 'helpers/browser';
import { runQueue } from 'helpers/queue';
import { KustomerErrorResponse, PublicCallback } from 'globals/types';
import { KustomerEventTypes } from 'core/core_main/types';
import { SendMessageParameters } from 'core_main/sdk/send_message/types';
import { GetMessagesParameters } from 'core_main/sdk/get_messages/types';
import { MarkReadParameters } from 'core_main/sdk/mark_read/types';
import { GetConversationParameters } from 'core_main/sdk/get_conversation/types';
import { GetConversationsParameters } from 'core_main/sdk/get_conversations/types';
import { EndConversationParameters } from 'core_main/sdk/end_conversation/types';
import { SubmitSatisfactionFormParameters } from 'core_main/sdk/submit_satisfaction_form/types';
import { GetTranslatedSatisfactionFormParameters } from 'core_main/sdk/get_translated_satisfaction_form/types';
import { SendTypingActivityParameters } from 'core_main/sdk/send_typing_activity/types';
import { LoginParameters } from 'core_main/sdk/login/types';
import { IsLoggedInParameters } from 'core_main/sdk/is_logged_in/types';
import { DescribeCustomerParameters } from 'core_main/sdk/describe_customer/types';
import { DescribeConversationParameters } from 'core_main/sdk/describe_conversation/types';
import { CreateConversationParameters } from 'core_main/sdk/create_conversation/types';
import { HandoffConversationParameters } from 'core_main/sdk/handoff/types';
import {
  GetArticlesCallbackResponse,
  GetArticlesParameters,
  GetArticleByIdParameters,
  ArticleCallbackResponse,
  GetArticleBySlugParameters,
} from 'core_main/sdk/get_articles/types';
import { SearchArticlesParameters } from 'core_main/sdk/search_articles/types';
import {
  InitAssistantParameters,
  SendKbDeflectionClickParameters,
} from 'core_main/api/assistant/types';
import {
  GetCategoriesCallbackResponse,
  GetCategoriesParameters,
} from 'core_main/sdk/get_categories/types';
import { IsUrlAllowedParameters } from 'core_main/sdk/is_url_allowed/types';
import { InitParameters } from 'core_main/sdk/init/types';
import { SendPresenceActivityParameters } from 'core_main/sdk/send_presence_activity/types';
import { GetAssistantParameters } from 'core_main/sdk/get_assistant/types';
import { GetKnowledgeBaseConfigCallbackResponse } from 'core_main/sdk/get_knowledge_base_config/types';
import { SubmitArticleFeedbackParameters } from 'core_main/sdk/submit_article_feedback/types';
import { UpdateArticleFeedbackParameters } from 'core_main/sdk/update_article_feedback/types';
import { ArticleFeedbackResponseData } from 'core_main/api/articles/types';
import {
  GetFeaturedArticlesCallbackResponse,
  GetFeaturedArticlesParameters,
} from 'core_main/sdk/get_featured_articles/types';
import { runMainMethod } from './helpers';
import {
  CountEventOptions,
  TimeEventOptions,
} from '../../globals/eventTracking/types';

export const getKustomerCoreWindow = (): KustomerCoreWindow => window;
export const getKustomerCoreObject = (): KustomerCore | undefined => {
  const kustomerCoreWindow = getKustomerCoreWindow();

  if (!kustomerCoreWindow) return undefined;

  return kustomerCoreWindow.KustomerCore;
};

export const setupCore = () => {
  const kustomerCore = getKustomerCoreObject();

  if (!kustomerCore) return;

  Assign(kustomerCore, {
    init: (parameters?: InitParameters, cb?: PublicCallback) =>
      runMainMethod('init', parameters, cb),
    createConversation: (
      parameters: CreateConversationParameters,
      cb?: PublicCallback,
    ) => runMainMethod('createConversation', parameters, cb),
    handoff: (
      parameters: HandoffConversationParameters,
      cb?: PublicCallback,
    ) => runMainMethod('handoff', parameters, cb),
    addListener: (type: KustomerEventTypes, cb?: PublicCallback) =>
      runMainMethod('addListener', type, cb),
    removeListener: (type: KustomerEventTypes, cb?: PublicCallback) =>
      runMainMethod('removeListener', type, cb),
    sendMessage: (parameters: SendMessageParameters, cb?: PublicCallback) =>
      runMainMethod('sendMessage', parameters, cb),
    getConversations: (parameters: GetConversationsParameters, cb) =>
      runMainMethod('getConversations', parameters, cb),
    getConversation: (parameters: GetConversationParameters, cb) =>
      runMainMethod('getConversation', parameters, cb),
    getMessages: (parameters: GetMessagesParameters, cb) =>
      runMainMethod('getMessages', parameters, cb),
    getHistoricalMessages: (parameters: GetMessagesParameters, cb) =>
      runMainMethod('getHistoricalMessages', parameters, cb),
    getAssistant: (parameters: GetAssistantParameters) =>
      runMainMethod('getAssistant', parameters),
    markRead: (parameters: MarkReadParameters) =>
      runMainMethod('markRead', parameters),
    markSessionAsRead: (sessionId: string) =>
      runMainMethod('markSessionAsRead', sessionId),
    getUnreadCount: () => runMainMethod('getUnreadCount'),
    endConversation: (parameters: EndConversationParameters, cb) =>
      runMainMethod('endConversation', parameters, cb),
    initAssistant: (parameters: InitAssistantParameters, cb) =>
      runMainMethod('initAssistant', parameters, cb),
    sendKbDeflectionClick: (parameters: SendKbDeflectionClickParameters, cb) =>
      runMainMethod('sendKbDeflectionClick', parameters, cb),
    isChatAvailable: () => runMainMethod('isChatAvailable'),
    getChatSettings: () => runMainMethod('getChatSettings'),
    submitSatisfactionForm: (
      parameters: SubmitSatisfactionFormParameters,
      cb,
    ) => {
      runMainMethod('submitSatisfactionForm', parameters, cb);
    },
    getTranslatedSatisfactionForm: (
      parameters: GetTranslatedSatisfactionFormParameters,
      cb,
    ) => {
      runMainMethod('getTranslatedSatisfactionForm', parameters, cb);
    },
    sendTypingActivity: (parameters: SendTypingActivityParameters) =>
      runMainMethod('sendTypingActivity', parameters),
    login: (parameters: LoginParameters, cb) =>
      runMainMethod('login', parameters, cb),
    isLoggedIn: (parameters: IsLoggedInParameters, cb) =>
      runMainMethod('isLoggedIn', parameters, cb),
    describeCustomer: (parameters: DescribeCustomerParameters, cb) =>
      runMainMethod('describeCustomer', parameters, cb),
    describeConversation: (parameters: DescribeConversationParameters, cb) =>
      runMainMethod('describeConversation', parameters, cb),
    getCategories: (
      parameters: GetCategoriesParameters,
      cb: (
        response: GetCategoriesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) => runMainMethod('getCategories', parameters, cb),
    getArticles: (
      parameters: GetArticlesParameters,
      cb: (
        response: GetArticlesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) => runMainMethod('getArticles', parameters, cb),
    getFeaturedArticles: (
      parameters: GetFeaturedArticlesParameters,
      cb: (
        response: GetFeaturedArticlesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) => runMainMethod('getFeaturedArticles', parameters, cb),
    getArticleById: (
      parameters: GetArticleByIdParameters,
      cb: (response: ArticleCallbackResponse | null, err?: Error) => void,
    ) => runMainMethod('getArticleById', parameters, cb),
    getArticleBySlug: (
      parameters: GetArticleBySlugParameters,
      cb: (response: ArticleCallbackResponse | null, err?: Error) => void,
    ) => runMainMethod('getArticleBySlug', parameters, cb),
    submitArticleFeedback: (
      parameters: SubmitArticleFeedbackParameters,
      cb: (response: ArticleFeedbackResponseData) => void,
    ) => runMainMethod('submitArticleFeedback', parameters, cb),
    updateArticleFeedback: (
      parameters: UpdateArticleFeedbackParameters,
      cb: (response: ArticleFeedbackResponseData) => void,
    ) => runMainMethod('updateArticleFeedback', parameters, cb),
    getKnowledgeBaseConfig: (
      cb: (response: GetKnowledgeBaseConfigCallbackResponse) => void,
    ) => runMainMethod('getKnowledgeBaseConfig', cb),
    searchArticles: (parameters: SearchArticlesParameters, cb) =>
      runMainMethod('searchArticles', parameters, cb),
    isUrlAllowed: (parameters: IsUrlAllowedParameters) =>
      runMainMethod('isUrlAllowed', parameters),
    logout: (cb) => runMainMethod('logout', cb),
    sendPresenceActivity: (parameters: SendPresenceActivityParameters) =>
      runMainMethod('sendPresenceActivity', parameters),
    stop: () => runMainMethod('stop'),
    kustomerEventCount(parameters: CountEventOptions) {
      runMainMethod('kustomerEventCount', {
        ...parameters,
        apiKey: kustomerCore.a,
      });
    },
    kustomerEventTimerStart(parameters: TimeEventOptions) {
      runMainMethod('kustomerEventTimerStart', {
        ...parameters,
        apiKey: kustomerCore.a,
      });
    },
    kustomerEventTimerStop(parameters: TimeEventOptions) {
      runMainMethod('kustomerEventTimerStop', {
        ...parameters,
        apiKey: kustomerCore.a,
      });
    },
  });
};

export const addMainReadyEventListener = (): void => {
  const kustomerCoreWindow = getKustomerCoreWindow();
  const kustomerCore = getKustomerCoreObject();

  kustomerCoreWindow.addEventListener('kustomer_core_main_ready', () => {
    if (!kustomerCore) return;

    setupCore();

    runQueue(kustomerCore);
  });
};

export const setupWindow = (): void => {
  addMainReadyEventListener();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const kustomerCoreFile = process.env.KUSTOMER_CORE_MAIN_INCLUDE!;

  // Find the script element that loads the core SDK (either in main document or iframe)
  const scriptEmbedElement = document.querySelectorAll(
    '[data-kustomer-api-key]',
  )[0] as HTMLScriptElement | null;

  // Get nonce from script element using the enhanced getNonce helper function
  // This supports both widget.js and core.js scenarios
  const nonce =
    (window as KustomerCoreWindow).KustomerGetNonce?.(scriptEmbedElement) ||
    scriptEmbedElement?.getAttribute('nonce') ||
    undefined;

  // If kustomer core was added to the window.document, then we need to create an iframe
  // This happens when a user is using our Core SDK
  const parent = getSafeParentContext(window);
  if (!parent || getKustomerCoreWindow() === parent) {
    /*
      We inject a <script> tag into the iframe with both:
      - id="kustomer-core-main" (for core.js-specific detection)
      - data-kustomer-api-key (for widget.js-style detection)
      This ensures that CSP nonce detection works for both widget.js and core.js scenarios,
      since the CSP nonce plugin and our helpers look for either attribute to find the nonce.
      The nonce attribute is also set if present, so dynamically loaded scripts in the iframe
      will receive the correct nonce and comply with CSP.
    */

    // Create the body element programmatically to avoid HTML injection
    const body = document.createElement('body');
    const script = document.createElement('script');
    script.id = 'kustomer-core-main';
    script.src = kustomerCoreFile;

    // Safely set the data-kustomer-api-key attribute
    const apiKey =
      scriptEmbedElement?.getAttribute('data-kustomer-api-key') || undefined;
    if (apiKey) {
      script.setAttribute('data-kustomer-api-key', apiKey);
    }

    // Set nonce if present
    if (nonce) {
      script.setAttribute('nonce', nonce);
    }

    body.appendChild(script);

    setupIframeForApi(
      'kustomer-core-sdk-iframe',
      'display: none',
      body.outerHTML,
      'Kustomer Core SDK Iframe',
      nonce,
    );
    return;
  }

  if (document.getElementById('kustomer-core-main')) return;

  const script = document.createElement('script');
  script.id = 'kustomer-core-main';
  script.src = kustomerCoreFile;
  if (nonce) {
    script.setAttribute('nonce', nonce);
  }
  appendElementToDocument(script);
};

setupWindow();
